import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import {
  useCreateJournalPhrase,
  useDeleteJournalPhrase,
  useJournalPhrases,
  useUpdateJournalPhrase,
  type JournalPhraseRow,
} from '@/hooks/useJournalPhrases';
import { cn } from '@/utils/cn';
import {
  canManageJournalPhrase,
  filterPhrasesByCreatorAndVisibility,
  isPhraseShortcutConflict,
  ownOrSharedJournalPhrases,
  phraseVisibilityLabel,
  searchJournalPhrases,
  type JournalPhraseVisibility,
  type PhraseVisibilityFilter,
} from '@/utils/journalPhrases';
import { JournalPhraseFormModal, type JournalPhraseFormValues } from './JournalPhraseFormModal';

export interface JournalPhrasesManagerProps {
  clinicId: string | null | undefined;
  /** Admin settings: every clinic phrase. Clinician/modal: own + shared. */
  scope: 'clinician' | 'admin';
  showPageChrome?: boolean;
}

export function JournalPhrasesManager({
  clinicId,
  scope,
  showPageChrome = false,
}: JournalPhrasesManagerProps) {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuth();
  const canCreateOwn = Boolean(clinicId && hasPermission(clinicId, 'journal_phrases', 'create'));
  const canShare = Boolean(clinicId && hasPermission(clinicId, 'journal_phrases', 'update'));
  const canAdminAll = Boolean(clinicId && hasPermission(clinicId, 'journal_phrases', 'delete'));
  const currentUserId = user?.id ?? null;
  const showAdminChrome = scope === 'admin';

  const phrasesQuery = useJournalPhrases(clinicId);
  const membersQuery = useClinicMembers(showAdminChrome ? clinicId : null);
  const createPhrase = useCreateJournalPhrase(clinicId);
  const updatePhrase = useUpdateJournalPhrase(clinicId);
  const deletePhrase = useDeleteJournalPhrase(clinicId);

  const [search, setSearch] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState<PhraseVisibilityFilter>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<JournalPhraseRow | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const creatorNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const member of membersQuery.data ?? []) {
      names[member.user_id] = member.name;
    }
    return names;
  }, [membersQuery.data]);

  const scoped = useMemo(() => {
    const rows = phrasesQuery.data ?? [];
    return showAdminChrome ? rows : ownOrSharedJournalPhrases(rows, currentUserId);
  }, [currentUserId, phrasesQuery.data, showAdminChrome]);

  const filtered = useMemo(() => {
    const byAdmin = showAdminChrome
      ? filterPhrasesByCreatorAndVisibility(scoped, creatorFilter, visibilityFilter)
      : scoped;
    return searchJournalPhrases(byAdmin, search);
  }, [creatorFilter, scoped, search, showAdminChrome, visibilityFilter]);

  const creatorOptions = useMemo(() => {
    const ids = Array.from(new Set(scoped.map((row) => row.created_by)));
    return ids
      .map((id) => ({
        id,
        name: creatorNames[id] || t('journal.phrases.unknown_user'),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'da'));
  }, [creatorNames, scoped, t]);

  const visibilityLabels = {
    private: t('journal.phrases.private'),
    shared: t('journal.phrases.shared'),
  };

  const openCreate = () => {
    setEditing(null);
    setFormMode('create');
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (row: JournalPhraseRow) => {
    setEditing(row);
    setFormMode('edit');
    setFormError(null);
    setFormOpen(true);
  };

  const handleSubmit = async (values: JournalPhraseFormValues) => {
    try {
      if (formMode === 'edit' && editing) {
        await updatePhrase.mutateAsync({
          id: editing.id,
          ...values,
          visibility: canShare ? values.visibility : 'private',
        });
        toast.success(t('journal.phrases.updated'));
      } else {
        await createPhrase.mutateAsync({
          ...values,
          visibility: canShare ? values.visibility : 'private',
        });
        toast.success(t('journal.phrases.created'));
      }
      setFormOpen(false);
      setEditing(null);
      setFormError(null);
    } catch (err) {
      setFormError(
        isPhraseShortcutConflict(err) ? t('journal.phrases.shortcut_conflict') : t('common.error'),
      );
    }
  };

  const handleToggleVisibility = async (row: JournalPhraseRow) => {
    const visibility: JournalPhraseVisibility = row.visibility === 'shared' ? 'private' : 'shared';
    try {
      await updatePhrase.mutateAsync({
        id: row.id,
        shortcut: row.shortcut,
        title: row.title,
        body: row.body,
        visibility,
      });
      toast.success(t('journal.phrases.updated'));
    } catch (err) {
      toast.error(
        isPhraseShortcutConflict(err) ? t('journal.phrases.shortcut_conflict') : t('common.error'),
      );
    }
  };

  const handleDelete = async (row: JournalPhraseRow) => {
    if (!window.confirm(t('journal.phrases.confirm_delete', { shortcut: row.shortcut }))) return;
    try {
      await deletePhrase.mutateAsync(row.id);
      toast.success(t('journal.phrases.deleted'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const canManage = (row: JournalPhraseRow) =>
    canManageJournalPhrase(row, currentUserId, canAdminAll) && (canAdminAll || canCreateOwn);

  return (
    <div>
      {showPageChrome && (
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ds-text">
              {t('journal.phrases.title')}
            </h2>
            <p className="mt-1 text-sm text-ds-muted">
              {t('journal.phrases.subtitle')}
            </p>
          </div>
          {canCreateOwn && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('journal.phrases.new')}
            </Button>
          )}
        </div>
      )}

      {!showPageChrome && canCreateOwn && (
        <div className="mb-4 flex justify-end">
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            {t('journal.phrases.new')}
          </Button>
        </div>
      )}

      <div className={cn('mb-4', showAdminChrome && 'flex flex-col gap-3 sm:flex-row')}>
        <div className="min-w-0 flex-1">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('journal.phrases.search')}
          />
        </div>
        {showAdminChrome && (
          <div className="flex flex-col gap-3 sm:flex-row">
            <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-ds-muted">
              {t('journal.phrases.created_by')}
              <Select
                value={creatorFilter}
                onChange={(event) => setCreatorFilter(event.target.value)}
              >
                <option value="all">{t('journal.phrases.filter_all_users')}</option>
                {creatorOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex min-w-[8rem] flex-col gap-1 text-xs text-ds-muted">
              {t('journal.phrases.visibility')}
              <Select
                value={visibilityFilter}
                onChange={(event) => {
                  const next = event.target.value;
                  if (next === 'all' || next === 'private' || next === 'shared') {
                    setVisibilityFilter(next);
                  }
                }}
              >
                <option value="all">{t('journal.phrases.filter_all_visibility')}</option>
                <option value="private">{t('journal.phrases.private')}</option>
                <option value="shared">{t('journal.phrases.shared')}</option>
              </Select>
            </label>
          </div>
        )}
      </div>

      {phrasesQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ds-field p-10 text-center">
          <p className="text-sm text-ds-placeholder">{t('journal.phrases.empty')}</p>
          <Button variant="link" className="mt-2" onClick={openCreate}>
            {t('journal.phrases.create')}
          </Button>
        </div>
      ) : showAdminChrome ? (
        <div className="overflow-hidden rounded-lg border border-ds-line">
          <table className="min-w-full divide-y divide-ds-line">
            <thead className="bg-ds-sunk">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-ds-muted"
                >
                  {t('journal.phrases.shortcut')}
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-ds-muted"
                >
                  {t('journal.phrases.name')}
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-ds-muted"
                >
                  {t('journal.phrases.visibility')}
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-ds-muted"
                >
                  {t('journal.phrases.created_by')}
                </th>
                <th scope="col" className="relative px-3 py-3">
                  <span className="sr-only">{t('common.edit')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-line bg-ds-card">
              {filtered.map((row) => {
                const visibility = row.visibility === 'shared' ? 'shared' : 'private';
                return (
                  <tr key={row.id}>
                    <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-ds-text">
                      /{row.shortcut}
                    </td>
                    <td className="max-w-xs px-3 py-3 text-sm text-ds-muted">
                      <p>{row.title || '—'}</p>
                      <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs">{row.body}</p>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <VisibilityBadge
                        visibility={visibility}
                        label={phraseVisibilityLabel(visibility, visibilityLabels)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-ds-text">
                      {creatorNames[row.created_by] || t('journal.phrases.unknown_user')}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {canManage(row) && (
                        <PhraseActions
                          visibility={visibility}
                          canShare={canShare}
                          onToggle={() => void handleToggleVisibility(row)}
                          onEdit={() => openEdit(row)}
                          onDelete={() => void handleDelete(row)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => {
            const visibility = row.visibility === 'shared' ? 'shared' : 'private';
            return (
              <div
                key={row.id}
                className="flex items-start gap-3 rounded-xl border border-ds-line bg-ds-card p-4 shadow-card"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-ds-text">/{row.shortcut}</p>
                    <VisibilityBadge
                      visibility={visibility}
                      label={phraseVisibilityLabel(visibility, visibilityLabels)}
                    />
                  </div>
                  {row.title && <p className="mt-0.5 text-xs text-ds-muted">{row.title}</p>}
                  <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-ds-muted">
                    {row.body}
                  </p>
                </div>
                {canManage(row) && (
                  <PhraseActions
                    visibility={visibility}
                    canShare={canShare}
                    onToggle={() => void handleToggleVisibility(row)}
                    onEdit={() => openEdit(row)}
                    onDelete={() => void handleDelete(row)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <JournalPhraseFormModal
        open={formOpen}
        mode={formMode}
        initial={
          editing
            ? {
                shortcut: editing.shortcut,
                title: editing.title ?? '',
                body: editing.body,
                visibility: editing.visibility === 'shared' ? 'shared' : 'private',
              }
            : { visibility: 'private' }
        }
        pending={createPhrase.isPending || updatePhrase.isPending}
        canShare={canShare}
        error={formError}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) => void handleSubmit(values)}
      />
    </div>
  );
}

function VisibilityBadge({
  visibility,
  label,
}: {
  visibility: JournalPhraseVisibility;
  label: string;
}) {
  return (
    <span
      className={cn(
        'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
        visibility === 'shared'
          ? 'bg-ds-teal-tint text-ds-teal'
          : 'bg-ds-neutral-tint text-ds-muted bg-ds-card',
      )}
    >
      {label}
    </span>
  );
}

function PhraseActions({
  visibility,
  canShare,
  onToggle,
  onEdit,
  onDelete,
}: {
  visibility: JournalPhraseVisibility;
  canShare: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 items-center gap-1">
      {canShare && (
        <Button type="button" variant="secondary" size="sm" onClick={onToggle}>
          {visibility === 'shared' ? t('journal.phrases.private') : t('journal.phrases.shared')}
        </Button>
      )}
      <Button
        type="button"
        variant="tertiary"
        size="icon"
        title={t('common.edit')}
        onClick={onEdit}
      >
        <Pencil />
      </Button>
      <Button
        type="button"
        variant="tertiary"
        size="icon"
        title={t('common.delete')}
        onClick={onDelete}
      >
        <Trash2 />
      </Button>
    </div>
  );
}
